import { getIconAsBgImage } from '@sportal365/smp-widget-utils';
import { FC } from 'react';
import { styled } from '../../../../../packages/theme/stitches.config';
import { EventTypeCode, IMatchEvent } from '../../../../../packages/types';

interface EventIconsProps {
  events: IMatchEvent[];
  displaySubstitution?: boolean;
  layout?: 'default' | 'corners';
}

const EventIcons: FC<EventIconsProps> = ({ events, displaySubstitution = true, layout = 'default' }) => {
  const goalIcons: Record<string, string> = {
    [EventTypeCode.GOAL]: 'goal',
    [EventTypeCode.OWN_GOAL]: 'own-goal',
  };
  const icons: JSX.Element[] = [];

  [EventTypeCode.GOAL, EventTypeCode.OWN_GOAL].forEach((goalTypeCode) => {
    const numberOfGoals = events.filter(({ typeCode }) => {
      return (typeCode === EventTypeCode.PENALTY_GOAL && goalTypeCode === EventTypeCode.GOAL) || typeCode === goalTypeCode;
    }).length;

    if (numberOfGoals === 1) {
      icons.push(<EventIcon key={goalTypeCode} style={{ backgroundImage: `url("${getIconAsBgImage(goalIcons[goalTypeCode])}")` }} />);
    } else if (numberOfGoals > 1) {
      icons.push(
        <EventIconContainer key={goalTypeCode + 'S'}>
          <EventIcon style={{ backgroundImage: `url("${getIconAsBgImage(goalIcons[goalTypeCode])}")` }} />
          <NumberCard>{numberOfGoals}</NumberCard>
        </EventIconContainer>,
      );
    }
  });

  if (events.find(({ typeCode }) => typeCode === EventTypeCode.RED_CARD)) {
    icons.push(<EventIcon key={EventTypeCode.RED_CARD} style={{ backgroundImage: `url("${getIconAsBgImage('red')}")` }} />);
  } else if (events.find(({ typeCode }) => typeCode === EventTypeCode.YELLOW_CARD_RED)) {
    icons.push(<EventIcon key={EventTypeCode.YELLOW_CARD_RED} style={{ backgroundImage: `url("${getIconAsBgImage('yellow-red')}")` }} />);
  } else if (events.find(({ typeCode }) => typeCode === EventTypeCode.YELLOW_CARD)) {
    icons.push(<EventIcon key={EventTypeCode.YELLOW_CARD} style={{ backgroundImage: `url("${getIconAsBgImage('yellow')}")` }} />);
  }

  if (displaySubstitution && events.find(({ typeCode }) => typeCode === EventTypeCode.SUBSTITUTION)) {
    icons.push(
      <EventIcon
        key={EventTypeCode.SUBSTITUTION}
        style={{ backgroundImage: `url("${getIconAsBgImage('substitution_with_background')}")` }}
      />,
    );
  }

  if (icons.length === 0) {
    return null;
  }

  if (layout === 'default') {
    return <EventIconsWrapper style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>{icons}</EventIconsWrapper>;
  }

  const positions = ['bottomLeft', 'bottomRight', 'topLeft', 'topRight'] as const;

  return (
    <EventIconsCornerWrapper>
      {icons.slice(0, 4).map((icon, index) => (
        <IconPosition key={index} position={positions[index]}>
          {icon}
        </IconPosition>
      ))}
    </EventIconsCornerWrapper>
  );
};

export const EventIconsWrapper = styled.withConfig({ componentId: 'LineupsPlayer', displayName: 'EventIconsWrapper' })('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const EventIconsCornerWrapper = styled.withConfig({ componentId: 'LineupsPlayer', displayName: 'EventIconsCornerWrapper' })('div', {
  width: '100%',
  height: '100%',
  position: 'relative',
});

export const IconPosition = styled.withConfig({ componentId: 'LineupsPlayer', displayName: 'IconPosition' })('div', {
  position: 'absolute',
  width: '16px',
  height: '16px',

  variants: {
    position: {
      bottomLeft: {
        bottom: '0px',
        left: '-8px',
      },
      bottomRight: {
        bottom: '0px',
        right: '-8px',
      },
      topLeft: {
        top: '0px',
        left: '-8px',
      },
      topRight: {
        top: '0px',
        right: '-8px',
      },
    },
  },
});

const EventIconContainer = styled.withConfig({ componentId: 'LineupsPlayer', displayName: 'EventIconContainer' })('div', {
  display: 'flex',
  position: 'relative',
  width: '20px',
});

const NumberCard = styled.withConfig({ componentId: 'LineupsPlayer', displayName: 'NumberCard' })('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute',
  left: '50%',
  top: '30%',
  width: '12px',
  height: '14px',
  borderRadius: '2px',
  backgroundColor: 'black',
  color: 'white',
  fontFamily: '$primaryFontFamily',
  fontSize: '$2',
  fontWeight: '500',
});

export const EventIcon = styled.withConfig({ componentId: 'LineupsPlayer', displayName: 'EventIcon' })('div', {
  width: '16px',
  height: '16px',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
});

export default EventIcons;
